// EV-139 / EV-330 — Proximity CHECK-IN prompt. While the PWA is open, periodically
// checks the user's GPS against /api/events/live-nearby. It only fires within ~50m
// (RADIUS_M) — i.e. you're basically AT the venue — so the prompt is a CHECK-IN
// ("you're here — check in?"), NOT an RSVP "are you going?" (that's for far/future
// intent on the event detail page). Tapping Check in opens the event, whose EV-297
// proximity-aware CTA shows the actual check-in control. Foreground-only — true
// background geofence needs the native APK / web-push (phase 2, see EV-139 notes).
import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { mapCfgNum, getMapCfg } from '@/lib/mapDisplayConfig';   // EV-394 — admin Event Settings (proximity)
import { getDeviceId } from '@/lib/deviceId';   // EV-424 — anon check-in dedup by device cookie

const POLL_MS = 45_000;       // re-check every 45s while open
const RADIUS_M = 50;          // "at venue" threshold (tightened from 300 — fires when you're basically there)
const FIRST_DELAY_MS = 5_000; // let the app settle before first check

type Nearby = {
  kind: 'screening' | 'event';
  venue_id: number; venue_name: string; venue_slug: string;
  distance_m: number; ref_id: string; event_slug: string | null; event_id: string | null;
  title: string; starts_at: string; ends_at: string;
  home_crest_url?: string | null; away_crest_url?: string | null;
};

const keyOf = (n: Nearby) => `${n.kind}:${n.ref_id}`;
// Persistent dedupe WITH cooldown so we never spam the same event. Once shown we
// won't re-prompt for COOLDOWN_MS, even across app reopens. (Was a per-session
// Set that reset on every reopen → re-fired the same card 4-5x. Customer-hostile.)
const seenKey = 'hl_proximity_seen_v2';
const COOLDOWN_MS = 6 * 60 * 60 * 1000; // 6h
type SeenMap = Record<string, number>;
const loadSeen = (): SeenMap => { try { return JSON.parse(localStorage.getItem(seenKey) || '{}'); } catch { return {}; } };
const saveSeen = (m: SeenMap) => { try { localStorage.setItem(seenKey, JSON.stringify(m)); } catch {} };
const seenRecently = (m: SeenMap, key: string) => { const t = m[key]; return !!t && (Date.now() - t) < COOLDOWN_MS; };

// Android Chrome BLOCKS `new Notification()` — the only way to show a real
// system-tray message from a PWA is via the service worker. requireInteraction
// keeps it in the tray until the user dismisses it (so it can't be missed).
async function showTray(title: string, body: string, tag: string) {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.ready;
      await reg.showNotification(title, {
        body, tag, icon: '/logo192.png', badge: '/logo192.png',
        requireInteraction: true, renotify: true, data: { url: '/events' },
      } as NotificationOptions);
    } else {
      new Notification(title, { body, tag }); // desktop fallback
    }
  } catch { /* notifications optional */ }
}

export default function ProximityWatcher() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState<Nearby | null>(null);
  const seen = useRef<SeenMap>(loadSeen());
  const promptOpen = useRef(false);
  // EV-420 (Patrick 2026-06-09) — the local `seen` map is PER-DEVICE, so a user
  // who already checked in on their tablet got re-prompted on their phone. Track
  // event_ids the SERVER says this user already checked into (events.event_checkins
  // is UNIQUE per (event_id,user_id)), so we never re-prompt across devices.
  const checkedIn = useRef<Set<string>>(new Set());

  const check = useCallback(() => {
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) return;
    if (promptOpen.current) return; // don't stack prompts
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          // EV-394 (Patrick 2026-06-08): admin Event Settings control the trigger.
          // inside-only (default) → only fire when basically INSIDE the venue (~12m,
          // a radius proxy for the boundary box) so it does NOT pop "38m away".
          // If inside-only is OFF → use the configured radius. (True polygon
          // containment is the ideal follow-up.)
          const insideOnly = getMapCfg()['event_proximity_inside_only'] !== false;
          const cfgRadius = mapCfgNum('event_proximity_radius_m', 50);
          const effRadius = insideOnly ? 12 : cfgRadius;
          const { data } = await api.get('/events/live-nearby', {
            params: { lat: latitude, lng: longitude, radius: effRadius },
          });
          const list: Nearby[] = (data?.nearby || []).filter(
            (n: Nearby) => !seenRecently(seen.current, keyOf(n))
              && n.distance_m <= effRadius
              && !(n.event_id && checkedIn.current.has(n.event_id)));
          if (list.length && !promptOpen.current) {
            // EV-420 — pick the first candidate the user has NOT already checked
            // into server-side (cross-device dedup). Anonymous users have no server
            // check-in, so the local `seen` map remains their only gate.
            let chosen: Nearby | null = null;
            for (const n of list) {
              if (n.event_id && !checkedIn.current.has(n.event_id)) {
                try {
                  // EV-420/EV-424 — public status endpoint works for BOTH authed
                  // (token auto-attached → by user) and anon (by device cookie),
                  // so we never re-prompt across the PWA / Chrome / tablet.
                  const { data: ci } = await api.get(`/events/${n.event_id}/checkin`, { params: { device_id: getDeviceId() } });
                  if (ci?.checked_in) {
                    checkedIn.current.add(n.event_id);
                    seen.current[keyOf(n)] = Date.now(); saveSeen(seen.current);
                    continue; // already checked in → don't prompt
                  }
                } catch { /* lookup failed — fall through and prompt */ }
              }
              chosen = n; break;
            }
            if (chosen && !promptOpen.current) {
              promptOpen.current = true;
              // mark shown NOW so it won't re-fire on every poll / reopen for COOLDOWN_MS
              seen.current[keyOf(chosen)] = Date.now(); saveSeen(seen.current);
              setPrompt(chosen);
              // fire a real Android system-tray message (via service worker)
              showTray(`📍 ${chosen.venue_name}`, t('event.checkin.notifBody', { title: chosen.title }), keyOf(chosen));
            }
          }
        } catch { /* network/endpoint errors are silent */ }
      },
      () => { /* permission denied / unavailable — silent */ },
      { enableHighAccuracy: true, maximumAge: 15_000, timeout: 10_000 },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let alive = true;
    let timer: any;
    const loop = () => { if (!alive) return; check(); timer = setTimeout(loop, POLL_MS); };
    timer = setTimeout(loop, FIRST_DELAY_MS);
    // re-check when the tab regains focus (user likely just arrived somewhere)
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { alive = false; clearTimeout(timer); document.removeEventListener('visibilitychange', onVis); };
  }, [check]);

  const openEvent = (n: Nearby) => {
    const slug = n.event_slug || n.event_id;
    if (slug) navigate(`/event/${slug}`);
    else navigate(`/?venue=${n.venue_slug}`);
  };

  const dismiss = (n: Nearby) => {
    seen.current[keyOf(n)] = Date.now(); saveSeen(seen.current);
    promptOpen.current = false;
    setPrompt(null);
  };
  const checkIn = (n: Nearby) => {
    seen.current[keyOf(n)] = Date.now(); saveSeen(seen.current);
    promptOpen.current = false;
    setPrompt(null);
    openEvent(n); // event detail's EV-297 proximity CTA shows the Check-in control
  };
  const enableAlerts = async () => {
    try {
      if ('Notification' in window && Notification.permission !== 'granted') {
        const perm = await Notification.requestPermission();
        // Fire immediately for the current prompt so you SEE the tray message right away.
        if (perm === 'granted' && prompt) {
          showTray(`📍 ${prompt.venue_name}`, t('event.checkin.notifBody', { title: prompt.title }), keyOf(prompt));
        }
      }
    } catch {}
  };

  if (!prompt) return null;
  const distTxt = prompt.distance_m < 1 ? t('event.checkin.distanceHere') : t('event.checkin.distanceAway', { m: Math.round(prompt.distance_m) });
  return (
    <div style={{
      position: 'fixed', left: 12, right: 12, bottom: 'calc(78px + env(safe-area-inset-bottom,0px))',
      zIndex: 9000, maxWidth: 520, marginInline: 'auto',
      background: '#fff', borderRadius: 16, boxShadow: '0 8px 30px rgba(0,0,0,0.22)',
      border: '1px solid #fde6cf', padding: '12px 14px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 26 }}>📍</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#9a3412' }}>
            {t('event.checkin.atVenuePrompt', { venue: prompt.venue_name, dist: distTxt })}
          </div>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {prompt.title}
          </div>
          <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 700 }}>{t('event.checkin.onNowHere')}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button onClick={() => checkIn(prompt)}
          style={{ flex: 1, padding: '10px', borderRadius: 10, border: 'none', background: '#E46E34', color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>
          {t('event.checkin.checkInButton')}
        </button>
        <button onClick={() => dismiss(prompt)}
          style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
          {t('engage.pushDismiss')}
        </button>
      </div>
      {'Notification' in window && Notification.permission === 'default' && (
        <button onClick={enableAlerts}
          style={{ marginTop: 8, width: '100%', background: 'none', border: 'none', color: '#9a3412', fontSize: 11, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
          {t('event.checkin.notifyMeButton')}
        </button>
      )}
    </div>
  );
}
